import { normalize } from '@/lib/utils';
import { Vendor, PresignedUrlResponse, ProductFeedPresignedUrlResponse, ImageConfig, ProductFeedAttributesPayload, VendorResponse } from '@/types';

const API_BASE_URL = 'https://nestingale.com/v1/api';

export async function fetchVendors(): Promise<Vendor[]> {
  console.log('Fetching vendors from:', `${API_BASE_URL}/vendors`);
  try {
    const response = await fetch(`${API_BASE_URL}/vendors`);

    console.log('Vendor API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Vendor API error response:', errorText);
      throw new Error(`Failed to fetch vendors: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data: VendorResponse = await response.json();
    console.log('Vendor API response data:', data);

    // Return the vendors array from the response
    return data.vendors || [];
  } catch (error) {
    console.error('Error fetching vendors:', error);
    throw error;
  }
}

export async function getPresignedUrl(
  fileName: string,
  vendorName: string
): Promise<PresignedUrlResponse> {
  const url = `${API_BASE_URL}/fileUpload?fileName=${encodeURIComponent(fileName)}&opType=PriceUpdate&vendor=${encodeURIComponent(vendorName)}`;
  console.log('Fetching presigned URL from:', url);

  try {
    const response = await fetch(url);

    console.log('Presigned URL API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Presigned URL API error response:', errorText);
      throw new Error(`Failed to get presigned URL: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('API response data:', data);
    console.log('Property names in response:', Object.keys(data));

    console.log('Presigned URL received:', data.preSignedUrl);
    return {
      presignedUrl: data.preSignedUrl
    };
  } catch (error) {
    console.error(`Error getting presigned URL for ${fileName} (${vendorName}):`, error);
    throw error;
  }
}

export async function uploadFileWithPresignedUrl(
  presignedUrl: string,
  file: File,
  fileName: string,
  vendorName: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  // Parse the presigned URL to extract required headers
  let contentType = file.type || 'application/octet-stream';
  let acl = null;
  let securityToken = null;

  try {
    const url = new URL(presignedUrl);

    // Extract Content-Type from URL parameters
    const urlContentType = url.searchParams.get('Content-Type');
    if (urlContentType) {
      contentType = decodeURIComponent(urlContentType);
    }

    // Extract x-amz-acl from URL parameters
    const urlAcl = url.searchParams.get('x-amz-acl');
    if (urlAcl) {
      acl = decodeURIComponent(urlAcl);
    }

    // Extract x-amz-security-token from URL parameters
    const urlSecurityToken = url.searchParams.get('x-amz-security-token');
    if (urlSecurityToken) {
      securityToken = decodeURIComponent(urlSecurityToken);
    }
  } catch (e) {
    console.warn('Could not parse presigned URL:', e);
  }

  try {
    // Build headers object
    const headers: Record<string, string> = {
      'Content-Type': contentType,
    };

    // Add x-amz-acl header if present in URL
    if (acl) {
      headers['x-amz-acl'] = acl;
    }

    // Add x-amz-security-token header if present in URL
    if (securityToken) {
      headers['x-amz-security-token'] = securityToken;
    }

    // Use fetch API for better handling of presigned URLs
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers,
    });

    console.log(`📡 S3 Upload API: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', errorText);
      throw new Error(`Upload failed with status: ${response.status} (${response.statusText}) - ${errorText}`);
    }

  } catch (error) {
    console.error(`Error uploading file ${fileName} for ${vendorName}:`, error);
    throw error;
  }
}

export async function triggerFileProcessing(vendorName: string): Promise<any> {
  const url = `${API_BASE_URL}/fileProcessStatus?vendor=${encodeURIComponent(vendorName)}&opType=ProductPrice`;
  console.log('Triggering file processing for vendor:', vendorName);
  console.log('Request URL:', url);

  try {
    const response = await fetch(url);

    console.log('File processing API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('File processing API error response:', errorText);
      throw new Error(`Failed to trigger file processing: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('File processing API response data:', data);
    return data;
  } catch (error) {
    console.error(`Error triggering file processing for ${vendorName}:`, error);
    throw error;
  }
}

export async function submitProductFeedAttributes(
  vendorName: string,
  nestingaleMap: Record<string, string | string[]>
): Promise<any> {
  const url = `${API_BASE_URL}/productFeedAttributes`;

  // Build the payload with all the mapping attributes
  const payload = {
    vendor: vendorName,
    nestingaleMap: nestingaleMap
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload),
    });

    console.log(`📡 Product Feed Attributes API: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product Feed Attributes API error response:', errorText);
      throw new Error(`Failed to submit product feed attributes: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error submitting product feed attributes:`, error);
    throw error;
  }
}

export async function processNextStep(
  task: string,
  productFeedProcessingId: number,
  imageConfig: ImageConfig
): Promise<any> {
  const url = `${API_BASE_URL}/processNextStep`;

  const payload = {
    task: task,
    productFeedProcessingId: productFeedProcessingId,
    imageConfig: imageConfig
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload),
    });

    console.log(`📡 Process Next Step API: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Process Next Step API error response:', errorText);
      throw new Error(`Failed to process next step: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error processing next step:`, error);
    throw error;
  }
}

export async function fetchProductFeedProcessList(vendorName?: string): Promise<any> {
  let url = `${API_BASE_URL}/fileProcessStatus?opType=ProductFeed`;

  if (vendorName) {
    url += `&vendor=${encodeURIComponent(vendorName)}`;
  }

  console.log('Fetching product feed process list');
  console.log('Request URL:', url);

  try {
    const response = await fetch(url);

    console.log('Product feed process list API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product feed process list API error response:', errorText);
      throw new Error(`Failed to fetch product feed process list: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Product feed process list API response data:', data);
    return data;
  } catch (error) {
    console.error(`Error fetching product feed process list:`, error);
    throw error;
  }
}

export async function calculateDiscountAnalysis(vendorName: string): Promise<any> {
  const baseWebhookUrl = 'https://nestingale.app.n8n.cloud/webhook/0464bd3e-d50b-4006-93f2-3e2a7ba40725';
  const webhookUrl = `${baseWebhookUrl}?vendorName=${encodeURIComponent(vendorName)}`;

  console.log('Calculating discount analysis for vendor:', vendorName);
  console.log('Request URL:', webhookUrl);

  try {
    const response = await fetch(webhookUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    console.log('Discount analysis API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Discount analysis API error response:', errorText);
      throw new Error(`Failed to calculate discount analysis: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Discount analysis API response data:', data);
    return data;
  } catch (error) {
    console.error(`Error calculating discount analysis for ${vendorName}:`, error);
    throw error;
  }
}

export async function fetchDiscountAnalysisHistory(vendorName?: string): Promise<any> {
  let url = `${API_BASE_URL}/discountAnalysisHistory`;

  if (vendorName) {
    url += `?vendor=${encodeURIComponent(vendorName)}`;
  }

  console.log('Fetching discount analysis history');
  console.log('Request URL:', url);

  try {
    const response = await fetch(url);

    console.log('Discount analysis history API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Discount analysis history API error response:', errorText);
      throw new Error(`Failed to fetch discount analysis history: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Discount analysis history API response data:', data);
    return data;
  } catch (error) {
    console.error(`Error fetching discount analysis history:`, error);
    throw error;
  }
}

export async function fetchDiscountAnalysisById(analysisId: string): Promise<any> {
  const url = `${API_BASE_URL}/discountAnalysisHistory/${encodeURIComponent(analysisId)}`;
  console.log('Fetching discount analysis by ID:', analysisId);
  console.log('Request URL:', url);

  try {
    const response = await fetch(url);

    console.log('Discount analysis by ID API response status:', response.status, response.statusText);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Discount analysis by ID API error response:', errorText);
      throw new Error(`Failed to fetch discount analysis by ID: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    console.log('Discount analysis by ID API response data:', data);
    return data;
  } catch (error) {
    console.error(`Error fetching discount analysis by ID ${analysisId}:`, error);
    throw error;
  }
}

export async function getProductFeedPresignedUrl(
  fileName: string,
  vendorName: string,
  templateRequired: boolean = true,
  templateName?: string,
  opType: string = 'ProductFeed'
): Promise<ProductFeedPresignedUrlResponse> {
  let url = `${API_BASE_URL}/fileUpload?fileName=${encodeURIComponent(fileName)}&opType=${opType}&vendor=${encodeURIComponent(vendorName)}&templateRequired=${templateRequired}`;

  if (templateName) {
    url += `&templateName=${encodeURIComponent(templateName)}`;
  }

  try {
    const response = await fetch(url);

    console.log(`📡 Presigned URL API: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product feed presigned URL API error response:', errorText);
      throw new Error(`Failed to get product feed presigned URL: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error getting product feed presigned URL for ${fileName} (${vendorName}):`, error);
    throw error;
  }
}

export async function fetchFileProcessStatus(vendorName: string, opType: string = 'ProductFeed'): Promise<any> {
  const url = `${API_BASE_URL}/fileProcessStatus?opType=${encodeURIComponent(opType)}&vendor=${encodeURIComponent(vendorName)}`;

  try {
    const response = await fetch(url);

    console.log(`📡 File Process Status API: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('File process status API error response:', errorText);
      throw new Error(`Failed to fetch file process status: ${response.status} ${response.statusText} - ${errorText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching file process status for ${vendorName}:`, error);
    throw error;
  }
}

interface ProductFileProcessingResponse {
  message: string;
  processId: number;
}

export async function fetchProductFileProcessingId(
  fileLocation: string,
  vendorName: string,
  fileName: string
): Promise<ProductFileProcessingResponse> {
  const url = `${API_BASE_URL}/productFeedFileProcess`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        vendorName,
        fileLocation,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Product file processing ID API error response:", errorText);
      throw new Error(
        `Failed to fetch product file processing ID: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    // Handle double-encoded JSON
    const raw = await response.json();
    const data: ProductFileProcessingResponse =
      typeof raw === "string" ? JSON.parse(raw) : raw;

    return data;
  } catch (error) {
    console.error(
      `Error fetching product file processing ID for ${vendorName} (${fileName}):`,
      error
    );
    throw error;
  }
}

export async function fetchProductFeedProcessingDetails(productFeedProcessingId: string | number, opType: string = 'ProductFeed', vendor?: string): Promise<any> {
  const url = new URL(`${API_BASE_URL}/fileProcessStatus`);
  url.searchParams.set("opType", opType);
  if (productFeedProcessingId != null) {
    url.searchParams.set("productFeedProcessingId", String(productFeedProcessingId));
  }

  if (vendor) {
    url.searchParams.set("vendor", vendor);
  }

  try {
    const response = await fetch(url.toString());
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product feed processing details API error response:', errorText);
      throw new Error(`Failed to fetch product feed processing details: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const data = await response.json();
    const normalizedData = data?.data?.map((item: any) => ({
      ...item,
      taskStatus: item?.taskStatus?.map((ts: any) => ({
        ...ts,
        task: normalize(ts?.task),
      })),
    }));
    return { data: normalizedData };
  } catch (error) {
    console.error(`Error fetching product feed processing details for ${vendor} (${productFeedProcessingId}):`, error);
    throw error;
  }
}

export async function fetchProductFeedAttributes(vendorName: string): Promise<any> {
  const url = `${API_BASE_URL}/productFeedAttributes?vendor=${encodeURIComponent(vendorName)}`
  try {
    if (!vendorName) {
      throw new Error("Vendor name is required to fetch product feed attributes.");
    }
    const response = await fetch(url);
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Product feed attributes API error response:', errorText);
      throw new Error(`Failed to fetch product feed attributes: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error fetching product feed attributes for ${vendorName}:`, error);
    throw error;
  }
}

export async function publishToAssistant(productFeedProcessingId: string | number): Promise<any> {
  const url = `https://zcv0nnbrt4.execute-api.us-east-1.amazonaws.com/dev/nest/v1/api/publishToAssistant`
  try {
    if (!productFeedProcessingId) {
      throw new Error("Product feed processing ID is required to publish to assistant.");
    }
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        productFeedProcessingId,
      }),
    });
    if (!response.ok) {
      const errorText = await response.text();
      console.error('Publish to assistant API error response:', errorText);
      throw new Error(`Failed to publish to assistant: ${response.status} ${response.statusText} - ${errorText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Error publishing to assistant for ${productFeedProcessingId}:`, error);
    throw error;
  }
}

